import { Filter } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { CircleCheck, CircleDot, Timer } from "lucide-react";

import { useConfigurationContext } from "../../atomic-crm/root/ConfigurationContext";
import { SectionCard } from "../data-display/SectionCard";
import { formatMoney, type DealRow } from "./useDashboardMetrics";

/**
 * Pipeline Overview, live deal-stage funnel from real workspace deals.
 * Estimates/quotes join this card when the estimates module ships (M2).
 */
export const PipelineOverviewCard = ({ deals }: { deals: DealRow[] }) => {
  const { dealStages, dealPipelineStatuses, currency } =
    useConfigurationContext();

  const isWon = (deal: DealRow) => dealPipelineStatuses.includes(deal.stage);
  const active = deals.filter((deal) => !deal.archived_at);

  const open = active.filter((deal) => !isWon(deal) && deal.stage !== "lost");
  const won = active.filter(isWon);
  const decided = active.filter((deal) => isWon(deal) || deal.stage === "lost");
  const winRate =
    decided.length > 0
      ? `${Math.round((won.length / decided.length) * 100)}%`
      : "n/a";

  const sum = (rows: DealRow[]) =>
    rows.reduce((total, deal) => total + (deal.amount ?? 0), 0);

  const funnel = dealStages
    .filter((stage) => stage.value !== "lost")
    .map((stage) => {
      const stageDeals = active.filter((deal) => deal.stage === stage.value);
      return {
        ...stage,
        count: stageDeals.length,
        total: sum(stageDeals),
      };
    });
  // Bars scale by value; when no deal carries an amount yet, fall back to
  // scaling by count so the funnel still communicates shape honestly.
  const hasAmounts = funnel.some((stage) => stage.total > 0);
  const weight = (stage: { total: number; count: number }) =>
    hasAmounts ? stage.total : stage.count;
  const maxWeight = Math.max(1, ...funnel.map(weight));

  return (
    <SectionCard
      icon={Filter}
      title="Pipeline Overview"
      subtitle="Deal stages and conversion"
      action="View deals"
      actionTo="/deals"
    >
      <div className="grid grid-cols-3 divide-x divide-border-subtle rounded-lg border border-border-subtle">
        <PipelineStat
          icon={Timer}
          value={String(open.length)}
          amount={formatMoney(sum(open), currency)}
          caption="open deals"
        />
        <PipelineStat
          icon={CircleCheck}
          value={String(won.length)}
          amount={formatMoney(sum(won), currency)}
          caption="won all-time"
        />
        <PipelineStat
          icon={CircleDot}
          value={winRate}
          amount=""
          caption="win rate"
        />
      </div>

      <p className="mt-4 mb-2 text-[10.5px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">
        Conversion funnel
      </p>
      <div className="flex flex-col gap-2.5">
        {funnel.map((stage) => (
          <div key={stage.value}>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[12.5px] font-medium text-foreground">
                {stage.label}
              </span>
              <span className="text-[12px] tabular-nums text-muted-foreground">
                {formatMoney(stage.total, currency)}
                <span className="ml-2 inline-block w-4 text-right font-medium text-foreground">
                  {stage.count}
                </span>
              </span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface-muted">
              <div
                className="h-full rounded-full bg-chart-1 transition-all"
                style={{ width: `${(weight(stage) / maxWeight) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
};

const PipelineStat = ({
  icon: Icon,
  value,
  amount,
  caption,
}: {
  icon: LucideIcon;
  value: string;
  amount: string;
  caption: string;
}) => (
  <div className="flex flex-col items-center gap-0.5 px-3 py-4 text-center">
    <div className="mb-1 flex size-7 items-center justify-center rounded-full border border-border-subtle bg-surface-muted">
      <Icon className="size-3.5 text-muted-foreground" strokeWidth={1.75} />
    </div>
    <span className="text-[22px] font-semibold leading-7 tracking-tight text-foreground">
      {value}
    </span>
    {amount ? (
      <span className="text-[12px] tabular-nums text-muted-foreground">
        {amount}
      </span>
    ) : null}
    <span className="text-[11.5px] text-muted-foreground">{caption}</span>
  </div>
);
